import { useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";

interface CoconutGroveHeroProps {
  h1?: string;
  subhead?: string;
  zips?: string[];
}

export default function CoconutGroveHero({ h1, subhead, zips }: CoconutGroveHeroProps) {
  const [imgError, setImgError] = useState(false);
  const { t, currentLang } = useLanguage();

  return (
    <section className="w-full h-[75vh] min-h-[600px] flex flex-col lg:flex-row overflow-hidden">
      <div className="w-full lg:w-1/2 bg-[#fefce8] flex flex-col justify-between p-8 lg:p-16">
        <div className="flex-1 flex flex-col justify-center">
          <span className="text-[#ea580c] uppercase text-xs tracking-[0.2em] font-semibold mb-4">
            {currentLang === "es"
              ? "Reparación de Ventanas en Coconut Grove"
              : "Window Repair for Coconut Grove Homes"}
          </span>
          <h1 className="text-[#064e3b] font-bold text-4xl lg:text-5xl leading-tight mb-6">
            {h1 || t("coconutGrove.h1")}
          </h1>
          <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-8 max-w-lg">
            {subhead || t("coconutGrove.subhead")}
          </p>

          {zips && zips.length > 0 && (
            <p className="text-xs text-gray-500 mb-6">
              {t("hood.zipCodes")}{zips.join(", ")}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="tel:+17863774452"
              className="inline-flex items-center justify-center bg-[#ea580c] text-white font-semibold px-8 py-3 rounded-md hover:bg-[#c2410c] transition whitespace-nowrap"
            >
              <span className="w-5 h-5 flex items-center justify-center mr-2">
                <i className="ri-phone-line"></i>
              </span>
              {t("hood.callNow")}
            </a>
            <a
              href="/contact/"
              className="inline-flex items-center justify-center border-2 border-[#064e3b] text-[#064e3b] font-semibold px-8 py-3 rounded-md hover:bg-[#064e3b] hover:text-white transition whitespace-nowrap"
            >
              {t("hood.getQuote")}
            </a>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-[#064e3b]/10">
          <blockquote className="text-[#064e3b] italic text-sm lg:text-base leading-relaxed mb-3">
            {currentLang === "es"
              ? "\u00ABCabaña bayfront construida en 1928. Reemplazaron tres operadores de manivela rotos y resellaron la puerta del balcón. Profesionales, silenciosos y respetuosos de la propiedad histórica.\u00BB"
              : "\u201CBayfront cottage built 1928. They replaced three broken crank operators and resealed the balcony door. Professional, quiet, and respectful of the historic property.\u201D"}
          </blockquote>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#fbbf24] flex items-center justify-center text-[#064e3b] font-bold text-sm">
              AD
            </div>
            <div>
              <p className="font-semibold text-[#064e3b] text-sm">Aurelia Dubois</p>
              <p className="text-gray-500 text-xs">Coconut Grove 33133</p>
            </div>
            <div className="ml-auto flex gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <span key={i} className="w-4 h-4 flex items-center justify-center text-[#fbbf24]">
                  <i className="ri-star-fill text-xs"></i>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 h-64 lg:h-auto relative">
        {imgError ? (
          <div className="w-full h-full bg-[#064e3b] flex items-center justify-center">
            <span className="text-white/60 text-sm">
              {currentLang === "es" ? "Coconut Grove Bayfront" : "Waterfront Coconut Grove"}
            </span>
          </div>
        ) : (
          <img
            src="https://storage.readdy-site.link/project_files/76915537-ee3f-4564-9a23-41bd98b5df27/f46d5f18-2287-4bc1-8715-99108d120e25_impact-windows-coconut-grove.jpg?v=125c2c4836ef32e5216c2b94703f6ce6"
            alt={currentLang === "es" ? "Reparación de ventanas en casas bayfront de Coconut Grove" : "Coconut Grove waterfront homes window repair"}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        )}
      </div>
    </section>
  );
}